//* Importing modules
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
//* Importing react-router hooks
import { useNavigate, useParams } from "react-router-dom";
//* Importing components from Bootstrap
import { Form, InputGroup, ProgressBar } from "react-bootstrap";
import { Typeahead } from "react-bootstrap-typeahead";
//* Importing icons
import { RiArrowGoBackLine, RiFilePdfFill } from "react-icons/ri";

//! Importing components
import DefaultButton from "../../../component/base/DefaultButton";
import LoadingSpinner from "../../../component/base/LoadingSpinner";
import { DescriptionField } from "../managerForm/DescriptionField";
import { QuantityField } from "../managerForm/QuantityField";
import { ErrorMessages } from "../../../component/common/ErrorMessages";
import { Attention } from "../../../component/base/Attention";
//! Importing custom hooks
import { useAttributions } from "../../../hooks/demand/attributions/useAtributions";
import { useTeachers } from "../../../hooks/teachers/useTeachers";
import { useDisciplines } from "../../../hooks/disciplines/useDisciplines";

//? Importing types
import type {
  CreateAttributionInput,
  UpdateAttributionInput,
} from "../../../hooks/demand/attributions/types/types";

//? Importing styles
import "./styles.css";

//! Importing constants
import { STORAGE_URL } from "../../../constants/consts";

type DemandsManagerFormProps = {
  editMode?: boolean;
};

type Discipline = {
  id: string;
  name: string;
};

type Teacher = {
  id: string;
  name: string;
};

var debounce = require("lodash.debounce");

/**
 * Component for managing the creation and editing of attributions.
 *
 * Features/Improvements:
 *  - Mostra botão de download para PDF/imagens
 *  - Não exibe links longos do backend (exibe ícone estilizado para PDFs e imagens)
 *  - Exibe link completo apenas para URLs externas
 *  - Mantém barra de progresso para arquivos de até 2MB
 *  - Melhora espaçamento entre a barra de progresso e outros elementos
 */
export function DemandsManagerForm({
  editMode = false,
}: DemandsManagerFormProps) {
  const navigate = useNavigate();
  const params = useParams<{ id: string; idAttribute: string }>();
  const token = localStorage.getItem("token") || "";

  const {
    loading: loadingAttributions,
    error: attributionsError,
    createAttribution,
    updateAttribution,
    getAttributionById,
    isCreating,
    isUpdating,
  } = useAttributions(token, params.id);

  const {
    teachers,
    loading: loadingTeachers,
    search: searchTeacherText,
    setSearch: setSearchTeacherText,
    error: teachersError,
  } = useTeachers(token);

  const {
    discipline,
    loading: loadingDisciplines,
    search: searchDisciplineText,
    setSearch: setSearchDisciplineText,
    error: disciplinesError,
  } = useDisciplines(token);

  // Local states
  const [description, setDescription] = useState<string>("");
  const [selectedDiscipline, setSelectedDiscipline] =
    useState<Discipline | null>(null);
  const [selectedTeacher, setSelectedTeacher] = useState<Teacher | null>(null);
  const [multipleChoiceQty, setMultipleChoiceQty] = useState<number>(0);
  const [essayQty, setEssayQty] = useState<number>(0);
  const [setQty, setSetQty] = useState<number>(0);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Attachment states
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [attachmentLink, setAttachmentLink] = useState<string>("");
  const [fileSizeError, setFileSizeError] = useState<string>("");
  const [removeAttachment, setRemoveAttachment] = useState<boolean>(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  /**
   * Handler for file selection.
   * Clears the link if a file is selected.
   * Also checks if file is <= 2MB, otherwise shows error.
   */
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const sizeInMB = file.size / 1024 / 1024;
      if (sizeInMB > 2) {
        setFileSizeError("O arquivo excede o limite de 2MB!");
        setAttachmentFile(null);
      } else {
        setFileSizeError("");
        setAttachmentFile(file);
        setAttachmentLink("");
        setRemoveAttachment(false); // Novo arquivo, desabilita a flag de remoção
      }
    }
  };

  /**
   * Handler for link change.
   * Clears the file selection if a link is entered.
   */
  const handleLinkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAttachmentLink(e.target.value);
    if (e.target.value.trim()) {
      setAttachmentFile(null);
      setFileSizeError("");
      setRemoveAttachment(false); // Nova URL, desabilita a flag de remoção
    }
  };

  /**
   * Remove the attachment (file or link) and set flag for removal.
   */
  const handleRemoveAttachment = () => {
    setAttachmentFile(null);
    setAttachmentLink("");
    setFileSizeError("");
    setRemoveAttachment(true);
  };

  /**
   * localProgressBar: shows how much of the 2MB limit the selected file uses.
   */
  const localProgressBar = useMemo(() => {
    if (attachmentFile) {
      const sizeInMB = attachmentFile.size / 1024 / 1024;
      const progress = Math.min((sizeInMB / 2) * 100, 100);
      return (
        <div style={{ marginTop: "10px" }}>
          <ProgressBar
            now={progress}
            label={`${progress.toFixed(0)}%`}
            variant={progress < 100 ? "info" : "danger"}
          />
          <small>{sizeInMB.toFixed(2)}MB de 2MB</small>
        </div>
      );
    }
    return null;
  }, [attachmentFile]);

  /**
   * Preview logic for the attachment:
   * - For a new upload (attachmentFile), shows a preview.
   * - For an attachmentLink:
   *    * If the link is from backend (starts with "attachments/"), exibe ícone estilizado para PDF ou imagem.
   *    * Caso contrário, exibe o link completo.
   */
  const previewSection = useMemo(() => {
    // Caso haja um arquivo selecionado (novo upload)
    if (attachmentFile) {
      const fileType = attachmentFile.type.toLowerCase();
      const objectURL = URL.createObjectURL(attachmentFile);
      const fileName = attachmentFile.name || "arquivo.pdf";

      if (fileType.includes("image")) {
        return (
          <div style={{ marginTop: "10px" }}>
            <strong>Image Preview:</strong>
            <img
              src={objectURL}
              alt="Preview"
              style={{
                display: "block",
                maxWidth: "200px",
                maxHeight: "200px",
                marginTop: "5px",
              }}
            />
            <div style={{ marginTop: "10px" }}>{localProgressBar}</div>

            {/* Botões em linha */}
            <div style={{ marginTop: "10px", display: "flex", gap: "10px" }}>
              {/* Botão de download (usa a URL local com atributo download) */}
              <a
                href={objectURL}
                download={fileName}
                style={{
                  backgroundColor: "#28a745",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  padding: "5px 10px",
                  textDecoration: "none",
                  cursor: "pointer",
                }}
              >
                Download
              </a>
              <button
                type="button"
                onClick={handleRemoveAttachment}
                style={{
                  backgroundColor: "#dc3545",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  padding: "5px 10px",
                  cursor: "pointer",
                }}
              >
                Remover
              </button>
            </div>
          </div>
        );
      } else if (fileType.includes("pdf")) {
        return (
          <div style={{ marginTop: "10px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <RiFilePdfFill style={{ fontSize: "1.8rem", color: "#d9534f" }} />
              <span style={{ fontWeight: "bold" }}>PDF</span>
            </div>
            <div style={{ marginTop: "10px" }}>{localProgressBar}</div>

            <div style={{ marginTop: "10px", display: "flex", gap: "10px" }}>
              <a
                href={objectURL}
                download={fileName}
                style={{
                  backgroundColor: "#28a745",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  padding: "5px 10px",
                  textDecoration: "none",
                  cursor: "pointer",
                }}
              >
                Download
              </a>
              <button
                type="button"
                onClick={handleRemoveAttachment}
                style={{
                  backgroundColor: "#dc3545",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  padding: "5px 10px",
                  cursor: "pointer",
                }}
              >
                Remover
              </button>
            </div>
          </div>
        );
      } else {
        return (
          <div style={{ marginTop: "10px" }}>
            <strong>Arquivo selecionado:</strong> {fileName}
            <div style={{ marginTop: "10px" }}>{localProgressBar}</div>
            <div style={{ marginTop: "10px" }}>
              <button
                type="button"
                onClick={handleRemoveAttachment}
                style={{
                  backgroundColor: "#dc3545",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  padding: "5px 10px",
                  cursor: "pointer",
                }}
              >
                Remover
              </button>
            </div>
          </div>
        );
      }
    } else if (attachmentLink.trim()) {
      const link = attachmentLink.trim();
      const lowerLink = link.toLowerCase();
      const isImage =
        lowerLink.endsWith(".jpg") ||
        lowerLink.endsWith(".jpeg") ||
        lowerLink.endsWith(".png") ||
        lowerLink.endsWith(".gif");
      const isPdf = lowerLink.endsWith(".pdf");
      const isBackendLink = link.startsWith("attachments/");

      const finalLink =
        isBackendLink && STORAGE_URL ? `${STORAGE_URL}/storage/${link}` : link;

      if (isImage && isBackendLink && STORAGE_URL) {
        return (
          <div style={{ marginTop: "10px" }}>
            <strong>Image Preview (Link):</strong>
            <img
              src={finalLink}
              alt="Preview"
              style={{
                display: "block",
                maxWidth: "200px",
                maxHeight: "200px",
                marginTop: "5px",
              }}
            />
            <div style={{ marginTop: "10px", display: "flex", gap: "10px" }}>
              <a
                href={finalLink}
                download
                style={{
                  backgroundColor: "#28a745",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  padding: "5px 10px",
                  textDecoration: "none",
                  cursor: "pointer",
                }}
              >
                Download
              </a>
              <button
                type="button"
                onClick={handleRemoveAttachment}
                style={{
                  backgroundColor: "#dc3545",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  padding: "5px 10px",
                  cursor: "pointer",
                }}
              >
                Remover
              </button>
            </div>
          </div>
        );
      } else if (isPdf && isBackendLink) {
        return (
          <div style={{ marginTop: "10px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <RiFilePdfFill style={{ fontSize: "1.8rem", color: "#d9534f" }} />
              <span style={{ fontWeight: "bold" }}>PDF</span>
            </div>
            <div style={{ marginTop: "10px", display: "flex", gap: "10px" }}>
              <a
                href={finalLink}
                download
                style={{
                  backgroundColor: "#28a745",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  padding: "5px 10px",
                  textDecoration: "none",
                  cursor: "pointer",
                }}
              >
                Download
              </a>
              <button
                type="button"
                onClick={handleRemoveAttachment}
                style={{
                  backgroundColor: "#dc3545",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  padding: "5px 10px",
                  cursor: "pointer",
                }}
              >
                Remover
              </button>
            </div>
          </div>
        );
      } else {
        return (
          <div style={{ marginTop: "10px" }}>
            <strong>Link do anexo:</strong>
            <a
              href={finalLink}
              target="_blank"
              rel="noopener noreferrer"
              style={{ marginLeft: "8px" }}
            >
              {link}
            </a>
            <div style={{ marginTop: "10px" }}>
              <button
                type="button"
                onClick={handleRemoveAttachment}
                style={{
                  backgroundColor: "#dc3545",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  padding: "5px 10px",
                  cursor: "pointer",
                }}
              >
                Remover
              </button>
            </div>
          </div>
        );
      }
    }

    return null;
  }, [
    attachmentFile,
    attachmentLink,
    localProgressBar,
    editMode,
    handleRemoveAttachment,
  ]);

  // Fetch data in edit mode
  useEffect(() => {
    if (editMode && params.idAttribute) {
      /**
       * Fetches the attribution data by ID and updates the state with the fetched data.
       * If the attribution is not found, sets a validation error.
       * If an error occurs during the fetch, sets a validation error with the error message.
       *
       * @async
       * @function fetchAttribution
       * @returns {Promise<void>} A promise that resolves when the fetch operation is complete.
       */ const fetchAttribution = async () => {
        try {
          const attribution = await getAttributionById(
            params.idAttribute || ""
          );
          if (attribution) {
            setDescription(attribution.description || "");
            setSelectedDiscipline(attribution.discipline);
            setSelectedTeacher(attribution.teacher);
            setMultipleChoiceQty(attribution.quantityObjective);
            setEssayQty(attribution.quantityDissertative);
            setSetQty(attribution.quantitySet);
            if (attribution.attachmentUrl) {
              setAttachmentLink(attribution.attachmentUrl);
              setRemoveAttachment(false);
            }
          } else {
            setValidationErrors(["Atribuição não encontrada."]);
          }
        } catch (error: any) {
          setValidationErrors([
            error.message || "Erro ao buscar a atribuição.",
          ]);
        }
      };
      fetchAttribution();
    }
  }, [editMode, params.idAttribute, getAttributionById]);

  /**
   * Handles the form submission for creating or updating an attribution.
   * Validates the form inputs and sets validation errors if any.
   * If the form is valid, it either updates an existing attribution or creates a new one.
   *
   * @param {React.FormEvent} e - The form submission event.
   * @returns {Promise<void>} - A promise that resolves when the submission is complete.
   *
   * @throws {Error} - Throws an error if there is an issue with saving the attribution.
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationErrors([]);
    const errors: string[] = [];

    if (!description.trim()) {
      errors.push("Descrição é obrigatória.");
    }
    if (!selectedDiscipline) {
      errors.push("Disciplina é obrigatória.");
    }
    if (!selectedTeacher) {
      errors.push("Professor é obrigatório.");
    }
    if (multipleChoiceQty < 0) {
      errors.push(
        "Quantidade de questões de múltipla escolha não pode ser negativa."
      );
    }
    if (essayQty < 0) {
      errors.push(
        "Quantidade de questões dissertativas não pode ser negativa."
      );
    }
    if (setQty < 0) {
      errors.push("Quantidade de conjuntos não pode ser negativa.");
    }
    if (multipleChoiceQty === 0 && essayQty === 0 && setQty === 0) {
      errors.push(
        "Pelo menos uma das quantidades de questões deve ser maior que zero."
      );
    }
    if (fileSizeError) {
      errors.push(fileSizeError);
    }
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    // Build the base object
    const attributionData: CreateAttributionInput | UpdateAttributionInput = {
      demand_id: params.id || "",
      teacher_id: selectedTeacher ? selectedTeacher.id : "",
      discipline_id: selectedDiscipline ? selectedDiscipline.id : "",
      description: description.trim(),
      quantity_objective: multipleChoiceQty,
      quantity_dissertative: essayQty,
      quantity_set: setQty,
      removeAttachment: removeAttachment,
    };

    if (attachmentFile) {
      (attributionData as CreateAttributionInput).attachment = attachmentFile;
    } else if (attachmentLink.trim()) {
      (attributionData as CreateAttributionInput).attachment =
        attachmentLink.trim();
    }

    try {
      if (editMode && params.idAttribute) {
        const updateData: UpdateAttributionInput = {
          demand_id: params.id || "",
          id: params.idAttribute,
          teacher_id: attributionData.teacher_id,
          discipline_id: attributionData.discipline_id,
          description: attributionData.description,
          quantity_objective: attributionData.quantity_objective,
          quantity_dissertative: attributionData.quantity_dissertative,
          quantity_set: attributionData.quantity_set,
        };

        if (attachmentFile) {
          updateData.attachment = attachmentFile;
        } else if (attachmentLink.trim()) {
          updateData.attachment = attachmentLink.trim();
        } else if (removeAttachment) {
          (updateData as any).removeAttachment = true;
        }

        console.log("updateData", updateData);
        await updateAttribution(updateData);
        navigate(-1);
      } else {
        const createdAttribution = await createAttribution(
          attributionData as CreateAttributionInput
        );
        if (createdAttribution && createdAttribution.id) {
          navigate(-1);
        }
      }
    } catch (error: any) {
      setValidationErrors([error.message || "Erro ao salvar a atribuição."]);
    }
  };

  /**
   * A debounced function that updates the search discipline text state.
   *
   * This function is memoized to ensure that the debounce function is not recreated on every render.
   * It uses a debounce delay of 300 milliseconds to limit the rate at which the search discipline text state is updated.
   *
   * @param query - The search query string to update the search discipline text state with.
   *
   * @returns A debounced function that takes a query string as an argument and updates the search discipline text state.
   */ const debouncedDisciplineSearch = useMemo(
    () =>
      debounce((query: string) => {
        setSearchDisciplineText(query);
      }, 300),
    [setSearchDisciplineText]
  );
  /**
   * Creates a debounced version of the teacher search function.
   * The debounced function delays the processing of the search input until after 300 milliseconds have elapsed since the last time it was invoked.
   *
   * @returns {Function} A debounced function that takes a query string as an argument and sets the search teacher text.
   *
   * @param {string} query - The search query string.
   *
   * @dependency {Function} setSearchTeacherText - The function to set the search teacher text.
   */
  const debouncedTeacherSearch = useMemo(
    () =>
      debounce((query: string) => {
        setSearchTeacherText(query);
      }, 300),
    [setSearchTeacherText]
  );

  /**
   * Handles the discipline search by using a debounced search function.
   *
   * @param {string} query - The search query string.
   */
  const handleDisciplineSearch = useCallback(
    (query: string) => {
      debouncedDisciplineSearch(query);
    },
    [debouncedDisciplineSearch]
  );
  /**
   * Handles the search for teachers by using a debounced search function.
   *
   * @param query - The search query string used to find teachers.
   */
  const handleTeacherSearch = useCallback(
    (query: string) => {
      debouncedTeacherSearch(query);
    },
    [debouncedTeacherSearch]
  );

  useEffect(() => {
    return () => {
      debouncedDisciplineSearch.cancel();
      debouncedTeacherSearch.cancel();
    };
  }, [debouncedDisciplineSearch, debouncedTeacherSearch]);

  /**
   * Generates a list of discipline options based on the search text.
   *
   * If the search text is less than 3 characters long, it returns the first 5 disciplines.
   * Otherwise, it filters the disciplines to include only those whose names contain the search text (case-insensitive),
   * and then returns the first 5 matches.
   *
   * @returns {Array} A list of up to 5 discipline options.
   */
  const disciplineOptions = useMemo(() => {
    if (searchDisciplineText.length < 3) {
      return discipline.slice(0, 5);
    }
    return discipline
      .filter((t) =>
        t.name.toLowerCase().includes(searchDisciplineText.toLowerCase())
      )
      .slice(0, 5);
  }, [discipline, searchDisciplineText]);

  /**
   * Generates a list of teacher options based on the search text.
   *
   * If the search text is less than 3 characters long, it returns the first 5 teachers.
   * Otherwise, it filters the teachers whose names include the search text (case-insensitive)
   * and returns the first 5 matches.
   *
   * @returns {Array} A list of up to 5 teacher options.
   */
  const teacherOptions = useMemo(() => {
    if (searchTeacherText.length < 3) {
      return teachers.slice(0, 5);
    }
    return teachers
      .filter((t) =>
        t.name.toLowerCase().includes(searchTeacherText.toLowerCase())
      )
      .slice(0, 5);
  }, [teachers, searchTeacherText]);


  /**
   * Combines various error messages into a single array of error strings.
   *
   * @returns {string[]} An array containing all the error messages from validationErrors, attributionsError, teachersError, and disciplinesError.
   *
   * @remarks
   * This hook uses `useMemo` to memoize the combined errors array, ensuring that the array is only recomputed when one of the dependencies changes.
   *
   * @param {string[]} validationErrors - An array of validation error messages.
   * @param {string} attributionsError - An error message related to attributions.
   * @param {string} teachersError - An error message related to teachers.
   * @param {string} disciplinesError - An error message related to disciplines.
   */ 
  const combinedErrors = useMemo(() => {
    const errors: string[] = [...validationErrors];
    if (attributionsError) errors.push(attributionsError);
    if (teachersError) errors.push(teachersError);
    if (disciplinesError) errors.push(disciplinesError);
    return errors;
  }, [validationErrors, attributionsError, teachersError, disciplinesError]);

  const clearErrors = useCallback(() => {
    setValidationErrors([]);
  }, []);

  return (
    <div className="row">
      <div className="col-12">
        <div className="card">
          <div className="card-body" id="card" style={{ overflow: "visible" }}>
            <h4
              className="mt-0 header-title"
              style={{ fontSize: "1.5rem", color: "black" }}
            >
              {editMode ? "Editar" : "Cadastrar"} Atribuição
            </h4>
            <p style={{ fontSize: "1rem", color: "black" }}>
              {editMode
                ? "Edite uma atribuição do seu sistema"
                : "Cadastre as atribuições do seu sistema"}
            </p>
            <br />

            <ErrorMessages
              messages={combinedErrors}
              clearErrors={clearErrors}
            />

            {editMode && loadingAttributions && (
              <div className="row">
                <div className="col-12 d-flex justify-content-center align-items-center my-3">
                  <LoadingSpinner loading />
                </div>
              </div>
            )}

            {!loadingAttributions && (
              <Form onSubmit={handleSubmit} style={{ position: "relative" }}>
                <DescriptionField
                  description={description}
                  setDescription={setDescription}
                />

                {/* Container de Upload (Arquivo ou Link) */}
                <InputGroup className="mb-3">
                  <div
                    className="upload-container"
                    style={{
                      border: "1px solid #ccc",
                      borderRadius: "8px",
                      padding: "16px",
                      marginBottom: "1rem",
                      width: "100%",
                    }}
                  >
                    <h5 style={{ marginBottom: "8px" }}>
                      Anexar Arquivo ou Inserir Link
                    </h5>
                    <small
                      style={{
                        color: "#666",
                        marginBottom: "8px",
                        display: "block",
                      }}
                    >
                      Suporta PDF, JPG, PNG até 2MB
                    </small>
                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        alignItems: "center",
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        style={{
                          backgroundColor: "#007bff",
                          color: "#fff",
                          border: "none",
                          borderRadius: "4px",
                          padding: "8px 12px",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <i
                          className="icon-upload"
                          style={{ marginRight: "4px" }}
                        ></i>
                        Selecionar Arquivo
                      </button>
                      <input
                        type="file"
                        accept="application/pdf,image/*"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        style={{ display: "none" }}
                      />
                      <input
                        type="text"
                        placeholder="Ou insira aqui a URL"
                        value={attachmentLink}
                        onChange={handleLinkChange}
                        // Desabilita o input se já houver um arquivo selecionado
                        disabled={!!attachmentFile}
                        style={{
                          flex: 1,
                          padding: "8px",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                        }}
                      />
                    </div>

                    {fileSizeError && (
                      <p style={{ color: "red", marginTop: "5px" }}>
                        {fileSizeError}
                      </p>
                    )}

                    {previewSection}
                  </div>
                </InputGroup>

                {/* Disciplina */}
                <InputGroup className="mb-3">
                  <div className="flex fdcolumn flex1">
                    <Form.Label>
                      Disciplina <Attention />
                    </Form.Label>
                    <Typeahead
                      id="discipline-typeahead"
                      labelKey="name"
                      onInputChange={handleDisciplineSearch}
                      onChange={(selected: any) => {
                        const selectedDisc = selected as Discipline[];
                        setSelectedDiscipline(
                          selectedDisc.length > 0 ? selectedDisc[0] : null
                        );
                      }}
                      onMenuToggle={(isOpen) => {
                        if (!isOpen) {
                          debouncedDisciplineSearch.cancel();
                        }
                      }}
                      options={disciplineOptions}
                      placeholder="Selecione ou digite o nome da disciplina..."
                      selected={selectedDiscipline ? [selectedDiscipline] : []}
                      minLength={0}
                      maxResults={5}
                      clearButton={!editMode}
                      disabled={editMode}
                      renderMenuItemChildren={(option: any) => {
                        const disciplineOption = option as Discipline;
                        return <span>{disciplineOption.name}</span>;
                      }}
                      emptyLabel={
                        searchDisciplineText.length >= 3
                          ? "Nenhuma opção encontrada"
                          : disciplineOptions.length === 0
                          ? "Nenhuma disciplina cadastrada"
                          : ""
                      }
                    />
                  </div>
                </InputGroup>

                {/* Professor */}
                <InputGroup className="mb-3">
                  <div className="flex fdcolumn flex1">
                    <Form.Label>
                      Professor <Attention />
                    </Form.Label>
                    <Typeahead
                      id="teacher-typeahead"
                      labelKey="name"
                      onInputChange={handleTeacherSearch}
                      onChange={(selected: any) => {
                        const selectedTeach = selected as Teacher[];
                        setSelectedTeacher(
                          selectedTeach.length > 0 ? selectedTeach[0] : null
                        );
                      }}
                      onMenuToggle={(isOpen) => {
                        if (!isOpen) {
                          debouncedTeacherSearch.cancel();
                        }
                      }}
                      options={teacherOptions}
                      placeholder="Selecione ou digite o nome do professor..."
                      selected={selectedTeacher ? [selectedTeacher] : []}
                      minLength={0}
                      maxResults={5}
                      clearButton={!editMode}
                      disabled={editMode}
                      renderMenuItemChildren={(option: any) => {
                        const teacherOption = option as Teacher;
                        return <span>{teacherOption.name}</span>;
                      }}
                      emptyLabel={
                        searchTeacherText.length >= 3
                          ? "Nenhuma opção encontrada"
                          : teacherOptions.length === 0
                          ? "Nenhum professor cadastrado"
                          : ""
                      }
                    />
                  </div>
                </InputGroup>

                {/* Quantidades */}
                <QuantityField
                  label="Quantidade de questões de múltipla escolha"
                  value={multipleChoiceQty}
                  setValue={setMultipleChoiceQty}
                />
                <QuantityField
                  label="Quantidade de questões dissertativas"
                  value={essayQty}
                  setValue={setEssayQty}
                />
                <QuantityField
                  label="Quantidade de questões de Texto em Comun"
                  value={setQty}
                  setValue={setSetQty}
                />

                <div className="w100 inline-flex jcsb margin-top-1">
                  <DefaultButton
                    bg="secondary"
                    text="Voltar"
                    icon={<RiArrowGoBackLine />}
                    to={"-1"}
                  />
                  <DefaultButton
                    type="submit"
                    bg="confirm"
                    text="Salvar atribuição"
                    disabled={isCreating || isUpdating}
                    loadingtext="Salvando atribuição"
                    loading={isCreating || isUpdating}
                  />
                </div>
              </Form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
